import { designTokens } from "@itandi/itandi-bb-ui";
import { Box } from "@itandi/itandi-bb-ui/Box";
import { Buttonv2 } from "@itandi/itandi-bb-ui/Buttonv2";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { InputCheckBox } from "@itandi/itandi-bb-ui/InputCheckBox";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { useEffect, useId, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import useSWRMutation from "swr/mutation";
import type { LoginStaffModel } from "~/components/common/user_login_modal/UserLoginModal";
import type { NskResponse } from "~/models/Api";
import { isSuccess } from "~/utils/api/api_handler";

type Props = {
	loginUser: LoginStaffModel;
	mutateSession: () => Promise<void>;
	onBack: () => void;
	onClickForgotPassword?: (id: number) => Promise<NskResponse>;
	onLogin: (id: number, password: string | null) => Promise<NskResponse>;
};

export function UserLoginModalPassword({
	loginUser,
	onBack,
	onLogin,
	onClickForgotPassword,
	mutateSession
}: Props) {
	const { notify } = useNotification();
	const formId = useId();
	const [showPassword, setShowPassword] = useState(false);
	const passwordRef = useRef<HTMLInputElement | null>(null);

	const { handleSubmit, register } = useForm<{ password: string }>({
		defaultValues: { password: "" }
	});

	const { ref: hookFormRef, ...registerPassword } = register("password");

	useEffect(() => {
		passwordRef.current?.focus();
	}, []);

	const { trigger, isMutating } = useSWRMutation(
		["staff-login", loginUser.id],
		async (_key, { arg }: { arg: string | null }) => onLogin(loginUser.id, arg)
	);

	const { trigger: triggerPasswordReset, isMutating: isSendingResetMail } =
		useSWRMutation(
			["forgot-password", loginUser.id],
			async (_key, { arg }: { arg: (id: number) => Promise<NskResponse> }) =>
				arg(loginUser.id)
		);

	const onSubmitLogin = handleSubmit(async ({ password }) => {
		const result = await trigger(password);
		if (isSuccess(result)) {
			await mutateSession();
			notify({ content: "ログインしました。", variant: "success" });
		} else {
			notify({
				content: `エラーが発生しました。${result.message ?? ""}`,
				variant: "error"
			});
		}
	});

	const onSubmitForgotPassword = async (): Promise<void> => {
		const result = await triggerPasswordReset(onClickForgotPassword!);
		if (isSuccess(result)) {
			notify({
				content: "パスワード再設定用のメールを送信しました。",
				variant: "success"
			});
		} else {
			notify({
				content: `エラーが発生しました。${result.message ?? ""}`,
				variant: "error"
			});
		}
	};

	return (
		<>
			<ModalBody>
				<form id={formId} noValidate onSubmit={onSubmitLogin}>
					<FormGroup mb={designTokens["spacing/s"]} variant="vertical">
						<FormControl label="パスワード">
							<Input
								type={showPassword ? "text" : "password"}
								{...registerPassword}
								ref={(el) => {
									hookFormRef(el);
									passwordRef.current = el;
								}}
							/>
						</FormControl>
					</FormGroup>
					<InputCheckBox
						checked={showPassword}
						label="パスワードを表示する"
						onChange={(e) => {
							setShowPassword(e.target.checked);
						}}
					/>
				</form>
				{onClickForgotPassword ? (
					<Box mt={designTokens["spacing/s"]}>
						<Buttonv2
							loading={isSendingResetMail}
							onClick={() => {
								void onSubmitForgotPassword();
							}}
							size="s"
							variant="tertiary"
						>
							パスワード再設定メールの送信
						</Buttonv2>
					</Box>
				) : null}
			</ModalBody>
			<ModalFooter
				leftArea={
					<Buttonv2 disabled={isMutating} onClick={onBack} variant="secondary">
						もどる
					</Buttonv2>
				}
				rightArea={
					<Buttonv2
						form={formId}
						loading={isMutating}
						type="submit"
						variant="primary"
					>
						ログイン
					</Buttonv2>
				}
			/>
		</>
	);
}
